import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLP, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Minus, Plus, Gift, Loader2, User, Mail, Phone, CalendarClock } from "lucide-react";
import { TicketSlide } from "@/components/TicketSlide";
import { PhoneInput, isValidPhone, formatPhoneDisplay } from "@/components/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { countWords } from "@/lib/giftMessage";

const fmtCL = (d) => d.toLocaleString("es-CL", {
  timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
}).replace(",", " a las") + " hrs";

function CancelDeadline({ workshop }) {
  const starts = workshop?.next_session?.starts_at;
  if (!starts) return null;
  const hours = workshop.policy_cancel_hours || 24;
  const deadline = new Date(new Date(starts).getTime() - hours * 3600 * 1000);
  const late = deadline < new Date();
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-2.5 text-xs ${late ? "border-amber-300 bg-amber-50 text-amber-800" : "border-primary/25 bg-primary/5"}`} data-testid="cancel-deadline">
      <CalendarClock className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
      <span>
        {late
          ? <>Esta sesión comienza en menos de {hours} horas: <strong>si cancelas, no aplicará la devolución.</strong></>
          : <>Puedes cancelar tu reserva hasta el <strong data-testid="cancel-deadline-date">{fmtCL(deadline)}</strong> ({hours} horas antes del inicio).</>}
      </span>
    </div>
  );
}

export function CheckoutDialog({ open, onOpenChange, workshop, giftOnly = false, selection = null }) {
  const perStudent = workshop?.sessions_per_student;
  const nSes = selection?.count || Number(perStudent) || (perStudent ? 0 : workshop?.sessions_count) || workshop?.sessions?.length || 1;
  const nSesText = selection?.count || !perStudent || Number(perStudent) ? `${nSes} ${nSes === 1 ? "sesión" : "sesiones"}` : `${perStudent} sesiones`;
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const isAuthed = !!(user && user.role === "customer");
  const [qty, setQty] = useState(1);
  const [isGift, setIsGift] = useState(giftOnly);
  useEffect(() => { if (giftOnly) setIsGift(true); }, [giftOnly, open]);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "" });
  const [gift, setGift] = useState({ recipient_name: "", recipient_phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { if (open) api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, [open]);
  const paymentsOn = cfg ? cfg.enabled : null;
  const free = paymentsOn === false;
  const saveKey = `vivo_checkout_${workshop?.id}`;
  useEffect(() => {
    if (!open) return;
    const s = JSON.parse(sessionStorage.getItem(saveKey) || "null");
    if (!s) return;
    setQty(s.qty || 1);
    if (s.isGift) { setIsGift(true); setGift(s.gift); }
    if (!isAuthed && s.form) setForm(s.form);
  }, [open]); // eslint-disable-line
  const [ticket, setTicket] = useState(null);
  useEffect(() => { if (!open) setTicket(null); }, [open]);
  const hadPhone = isAuthed && !!user.phone;

  useEffect(() => {
    if (open && isAuthed) {
      setForm({ name: user.name || "", email: user.email || "", whatsapp: user.phone || "" });
    }
  }, [open, isAuthed, user]);

  const maxQty = Math.min(workshop?.spots_left ?? 10, 10);
  const unit = workshop?.offer_active ? (workshop?.effective_price_clp ?? workshop?.price_clp ?? 0) : (workshop?.price_clp ?? 0);
  const total = unit * qty;
  const baseTotal = (workshop?.price_clp || 0) * qty;

  const postRedirect = (url, token) => {
    const f = document.createElement("form");
    f.method = "POST"; f.action = url;
    const i = document.createElement("input");
    i.type = "hidden"; i.name = "token_ws"; i.value = token;
    f.appendChild(i); document.body.appendChild(f); f.submit();
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Completa nombre, email y WhatsApp");
      return;
    }
    if (!isValidPhone(form.whatsapp)) {
      toast.error("Ingresa un celular válido para el país seleccionado");
      return;
    }
    if (isGift && (!gift.recipient_name.trim() || !gift.message.trim())) {
      toast.error("Indica el nombre de quien recibe el regalo y tu mensaje");
      return;
    }
    if (isGift && !isValidPhone(gift.recipient_phone)) {
      toast.error("Ingresa un celular válido de quien recibe el regalo");
      return;
    }
    if (isGift && countWords(gift.message) > 20) {
      toast.error("Tu mensaje puede tener hasta 20 palabras");
      return;
    }
    setLoading(true);
    if (isAuthed && !hadPhone) {
      try { await api.put("/auth/phone", { phone: form.whatsapp }); await checkAuth?.(); }
      catch { /* no bloquear el pago si falla el guardado del teléfono */ }
    }
    const payload = {
      workshop_id: workshop.id, quantity: qty, customer: form,
      es_regalo: isGift, gift: isGift ? gift : null, origin_url: window.location.origin,
      group_id: selection?.group_id || null, session_ids: selection?.session_ids || null,
    };
    try {
      const { data } = await api.post("/student/checkout", payload);
      sessionStorage.removeItem(saveKey); setTicket(data.ticket); setLoading(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  if (ticket) {
    return (
      <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) window.location.reload(); }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md max-h-[94vh] overflow-y-auto p-4 sm:p-6" data-testid="ticket-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">{ticket.es_regalo ? <Gift className="w-5 h-5 text-primary" /> : <CalendarClock className="w-5 h-5 text-primary" />}{ticket.es_regalo ? "¡Regalo listo!" : "¡Reserva confirmada!"}</DialogTitle>
            <DialogDescription>{ticket.es_regalo ? `Ahora envíaselo a ${ticket.gift?.recipient_name} por WhatsApp con el botón de abajo.` : "Este es tu ticket. También lo encontrarás en Mis Tickets y en tu correo."}</DialogDescription>
          </DialogHeader>
          <TicketSlide r={ticket} onCancel={async () => { await api.post(`/reservations/${ticket.id}/cancel`); window.location.reload(); }} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => { window.location.href = "/explorar"; }} data-testid="preview-explore-btn">Seguir explorando</Button>
            <Button onClick={() => { window.location.href = "/mis-tickets"; }} data-testid="preview-tickets-btn">Ver en Mis Tickets</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reservar tu cupo</DialogTitle>
          <DialogDescription>
            {workshop?.name}{selection?.label ? ` · ${selection.label}` : ""} · {nSesText}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Cantidad de cupos</Label>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="h-8 w-8"
                onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus"
                disabled={qty <= 1}>
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-6 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <Button variant="outline" size="icon" className="h-8 w-8"
                onClick={() => setQty((q) => Math.min(maxQty, q + 1))} data-testid="qty-plus"
                disabled={qty >= maxQty}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Separator />

          {isAuthed ? (
            <div className="space-y-3" data-testid="checkout-prefilled">
              <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Datos del asistente</p>
                <p className="flex items-center gap-2 text-sm" data-testid="prefilled-name"><User className="w-4 h-4 text-primary" />{form.name}</p>
                <p className="flex items-center gap-2 text-sm" data-testid="prefilled-email"><Mail className="w-4 h-4 text-primary" />{form.email}</p>
                {hadPhone && (
                  <p className="flex items-center gap-2 text-sm" data-testid="prefilled-phone"><Phone className="w-4 h-4 text-primary" />{formatPhoneDisplay(form.whatsapp)}</p>
                )}
              </div>
              {!hadPhone && (
                <div>
                  <Label>Tu teléfono móvil</Label>
                  <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testId="checkout-whatsapp" />
                  <p className="text-xs text-muted-foreground mt-1">Lo guardaremos en tu perfil para que no te lo pidamos de nuevo.</p>
                </div>
              )}
              <button type="button" data-testid="edit-profile-link"
                onClick={() => { onOpenChange(false); navigate("/mis-tickets"); }}
                className="text-xs font-medium text-primary hover:underline">
                ¿No eres tú o quieres actualizar tus datos? Edita tu perfil aquí
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label htmlFor="co-name">Nombre</Label>
                <Input id="co-name" data-testid="checkout-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tu nombre" />
              </div>
              <div>
                <Label htmlFor="co-email">Email</Label>
                <Input id="co-email" type="email" data-testid="checkout-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tucorreo@ejemplo.cl" />
              </div>
              <div>
                <Label htmlFor="co-wa">WhatsApp</Label>
                <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testId="checkout-whatsapp" />
              </div>
              <p className="text-xs text-muted-foreground">Reserva como invitado · sin crear contraseña</p>
            </div>
          )}

          {workshop?.gift_enabled && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Gift className="w-4 h-4 text-accent" />Comprar para regalar</Label>
                <Switch checked={isGift} onCheckedChange={setIsGift} disabled={giftOnly} data-testid="gift-toggle" />
              </div>
              {giftOnly && <p className="text-xs text-muted-foreground" data-testid="gift-only-note">Ya tienes este taller: esta compra es un regalo para otra persona.</p>}
              {isGift && (
                <div className="space-y-3 animate-fade-up">
                  <Input placeholder="Nombre de quien recibe" data-testid="gift-recipient" maxLength={60}
                    value={gift.recipient_name}
                    onChange={(e) => setGift({ ...gift, recipient_name: e.target.value })} />
                  <div>
                    <Label className="text-xs">Celular de quien recibe</Label>
                    <PhoneInput value={gift.recipient_phone} onChange={(v) => setGift({ ...gift, recipient_phone: v })} testId="gift-phone" />
                  </div>
                  <div>
                    <Textarea placeholder="Tu mensaje (hasta 20 palabras)" data-testid="gift-message" maxLength={200}
                      value={gift.message}
                      onChange={(e) => setGift({ ...gift, message: e.target.value })} />
                    <p className={`text-xs mt-1 text-right ${countWords(gift.message) > 20 ? "text-destructive" : "text-muted-foreground"}`} data-testid="gift-word-count">{countWords(gift.message)}/20 palabras</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Al confirmar podrás enviarle el regalo por WhatsApp desde tu teléfono.</p>
                </div>
              )}
            </>
          )}

          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Valor del taller</span>
            <span className="text-sm flex items-center gap-1.5" data-testid="checkout-base">
              {workshop?.offer_active && <span className="text-muted-foreground line-through">{CLP(baseTotal)}</span>}
              <span className={workshop?.offer_active ? "text-primary font-semibold" : ""}>{CLP(total)}</span>
            </span>
          </div>
          {workshop?.offer_active && (
            <div className="flex items-center justify-between text-xs text-primary" data-testid="checkout-offer-note">
              <span>Descuento{workshop?.offer?.discount_type !== "fixed" ? ` (-${workshop?.offer?.discount_value}%)` : ""}</span>
              <span>- {CLP(baseTotal - total)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-lg font-display font-bold pt-1">
            <span>Valor del taller</span>
            <span data-testid="checkout-total">{total === 0 ? "GRATIS" : CLP(total)}</span>
          </div>
          <div className="rounded-lg border border-primary/25 bg-primary/5 p-2.5 text-xs" data-testid="free-reservation-note">
            <strong>Reservar en VIVOTaller es gratis.</strong> Tu cupo queda confirmado al instante. El valor del taller lo conversas directamente con el Maestro.
          </div>

          <Button className="w-full h-12 text-base" onClick={submit} disabled={loading} data-testid="checkout-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Reservar (${nSesText})`}
          </Button>
          <CancelDeadline workshop={workshop} />
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="cancellation-policy-note">
            <strong className="text-foreground">Política de cancelación:</strong> puedes cancelar tu reserva dentro del plazo de antelación que fija el taller. Cualquier devolución de dinero se acuerda directamente con el Maestro.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
